import React, { useMemo, useState } from "react";
import { useBetSlip } from "../providers/BetSlipProvider";
import { useBonuses } from "../providers/BonusProvider";
import {
  getBonusAmount,
  resolveFreeBetConditions,
  selectActiveBonuses,
} from "../shared/utils/bonus-utils";
import { useCalculateOdds } from "../hooks/useCalculateOdds";
import { useI18n } from "../providers/I18nProvider";

interface FreeBetsProps {
  useFreeBet: boolean;
  setUseFreeBet: (useFreeBet: boolean) => void;
}

export const FreeBets: React.FC<FreeBetsProps> = ({
  useFreeBet,
  setUseFreeBet,
}) => {
  const {
    betSlipState,
    selectedBonus,
    selectBonus,
    updateGlobalStake,
    config,
  } = useBetSlip();
  const { t, tHtml } = useI18n();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { rawBonuses } = useBonuses();
  const mappedBonuses = useMemo(
    () =>
      selectActiveBonuses(
        rawBonuses,
        config.brand.staticBonuses,
        config.user.currencyId,
      ).freeBets,
    [rawBonuses, config.brand.staticBonuses, config.user.currencyId],
  );

  const totalSelections =
    betSlipState.selections.length +
    (betSlipState.betBuilderState?.totalOdds?.length || 0);
  const comboTotalOdds = useCalculateOdds({
    selections: betSlipState.selections,
    betBuilderOdds: betSlipState.betBuilderState?.totalOdds || [],
    mode: betSlipState.mode,
  });
  const singleSelectionOddPrice =
    totalSelections === 1 ? betSlipState.selections[0]?.oddPrice : undefined;

  const selectedBonusConditions = selectedBonus
    ? resolveFreeBetConditions(
        selectedBonus,
        betSlipState.mode,
        totalSelections,
        comboTotalOdds,
        singleSelectionOddPrice,
      )
    : null;

  const handleBonusSelect = (mappedBonus: MappedBonus) => {
    selectBonus(mappedBonus.originalBonus);
    updateGlobalStake(getBonusAmount(mappedBonus.originalBonus));
    setIsDropdownOpen(false);
  };

  const handleCheckboxChange = (checked: boolean) => {
    updateGlobalStake(0);
    setUseFreeBet(checked);
    if (!checked) {
      selectBonus(null);
      setIsDropdownOpen(false);
    }
  };
  const hasItems = mappedBonuses.length > 0;

  if (!hasItems) {
    return null;
  }

  return (
    <div className="w-full mb-4 px-4">
      <div className="flex items-start justify-between gap-3">
        {/* Checkbox to enable/disable free bet */}
        <div className="flex items-center gap-2 mt-1">
          <input
            type="checkbox"
            id="use-free-bet"
            checked={useFreeBet}
            onChange={(e) => handleCheckboxChange(e.target.checked)}
            className="w-4 h-4 accent-input-accent cursor-pointer"
          />
          <label
            htmlFor="use-free-bet"
            className="text-sm cursor-pointer select-none whitespace-nowrap"
          >
            {t("BetSlipUseFrb")}
          </label>
          <div className="flex relative group">
            <i className="au-i-info-thin-circle"></i>
            <div
              className="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap"
              dangerouslySetInnerHTML={tHtml("BetSlipUseFrbInformation")}
            />
          </div>
        </div>

        {/* Dropdown - horizontally aligned with checkbox */}
        {useFreeBet && mappedBonuses && mappedBonuses.length > 0 && (
          <div className="relative flex-none basis-1/2">
            <div
              className="flex items-center justify-between p-2 bg-input-bg text-input-text border border-solid border-input-stake-border rounded-4 cursor-pointer"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              {selectedBonus ? (
                <div className="flex items-center gap-2 flex-1">
                  <i
                    className={
                      selectedBonusConditions?.isEligible
                        ? "au-i-filled-approved-checkmark text-success"
                        : "au-i-filled-exclamation text-warning"
                    }
                  ></i>
                  <span className="text-sm font-semibold">
                    {getBonusAmount(selectedBonus)} {config.user.currency}
                  </span>
                </div>
              ) : (
                <span className="text-sm">{t("SelectFrbBonus")}</span>
              )}
              <svg
                className={`w-3 h-3 shrink-0 transform transition-transform ${
                  isDropdownOpen ? "rotate-180" : ""
                }`}
                viewBox="0 0 12 8"
                fill="none"
              >
                <path
                  d="M1 1.5L6 6.5L11 1.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {isDropdownOpen && (
              <ul className="absolute z-10 w-full mt-1 bg-input-bg border border-solid border-input-border rounded max-h-64 overflow-y-auto">
                {mappedBonuses.map((bonus) => {
                  const conditions = resolveFreeBetConditions(
                    bonus.originalBonus,
                    betSlipState.mode,
                    totalSelections,
                    comboTotalOdds,
                    singleSelectionOddPrice,
                  );
                  return (
                    <li
                      key={bonus.id}
                      className={`p-3 cursor-pointer border-b last:border-b-0 ${
                        selectedBonus?.id === bonus.originalBonus.id
                          ? "bg-input-bg"
                          : "bg-input-bg"
                      }`}
                      onClick={() => handleBonusSelect(bonus)}
                    >
                      <div className="flex items-center gap-2">
                        {conditions.isEligible ? (
                          <i className="au-i-filled-approved-checkmark text-success"></i>
                        ) : (
                          <i className="au-i-filled-exclamation text-warning"></i>
                        )}
                        <div className="flex-1">
                          <div className="font-semibold text-sm mb-1">
                            {bonus.amount} {config.user.currency}
                          </div>
                          {!conditions.isEligible && (
                            <>
                              <div className="text-sm text-body-text">
                                {t("BetSlipWidgetFrbMinOdds")}:{" "}
                                {bonus.originalBonus.minTotalOdds ||
                                  bonus.originalBonus.minOddPerRow}
                              </div>
                              <div className="text-sm text-body-text">
                                {t("BetSlipWidgetFrbMinSelections")}:{" "}
                                {bonus.originalBonus.minSelection}
                              </div>
                            </>
                          )}
                          {bonus.percentage > 0 && (
                            <div className="text-xs font-semibold mt-1">
                              Bonus: {bonus.percentage}%
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            {selectedBonusConditions && !selectedBonusConditions.isEligible && (
              <div className="text-sm text-danger mt-1">
                {t("NotEligibleFrb")}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FreeBets;
